/** Stores pending Completions in IndexedDB for the enclosing Completion log. */
import type { CompletionOutbox } from '../completionLog.js';

const STORE_NAME = 'completions';

const openDatabase = (factory: IDBFactory, databaseName: string): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = factory.open(databaseName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open completion outbox.'));
  });

const transact = async <Result>(
  factory: IDBFactory,
  databaseName: string,
  mode: IDBTransactionMode,
  start: (store: IDBObjectStore) => IDBRequest<Result>,
): Promise<Result> => {
  const database = await openDatabase(factory, databaseName);
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const request = start(transaction.objectStore(STORE_NAME));
    let result: Result;

    request.onsuccess = () => {
      result = request.result;
    };
    transaction.oncomplete = () => {
      database.close();
      resolve(result);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error ?? new Error('Completion outbox transaction failed.'));
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error ?? new Error('Completion outbox transaction was aborted.'));
    };
  });
};

export const createIndexedDbOutbox = (
  factory: IDBFactory,
  databaseName = 'life-rpg-completion-outbox',
): CompletionOutbox => ({
  put: async (completion) => {
    await transact(factory, databaseName, 'readwrite', (store) => store.put(completion));
  },
  list: () => transact(factory, databaseName, 'readonly', (store) => store.getAll()),
  remove: async (id) => {
    await transact(factory, databaseName, 'readwrite', (store) => store.delete(id));
  },
});
